import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail, CreditCard } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-orange-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm px-4 sm:px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-text-medium hover:text-mint-green transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to TypeTown</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-mint-green" />
            <span className="font-display font-bold text-text-dark">Privacy Policy</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-mint-green/10 rounded-full mb-4">
            <Lock className="w-8 h-8 text-mint-green" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-text-dark mb-2">
            Privacy Policy
          </h1>
          <p className="text-text-medium">Last updated: March 2026</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-coral-orange" />
              Our Commitment to Your Privacy
            </h2>
            <p className="text-text-medium leading-relaxed">
              TypeTown is committed to protecting the privacy of children and families who use our typing tutor application. 
              We understand that when children use our service, their safety and privacy are of paramount importance. 
              This policy explains what information we collect, how we use it, and how we protect it.
            </p>
          </section>

          {/* Data We Collect */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-sky-blue" />
              Information We Collect
            </h2>
            <div className="space-y-4 text-text-medium leading-relaxed">
              <div>
                <h3 className="font-bold text-text-dark mb-2">Account Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Parent/guardian email address (for account management)</li>
                  <li>Child's first name (for personalisation)</li>
                  <li>Child's age (to set appropriate difficulty levels)</li>
                  <li>Password (securely encrypted)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-text-dark mb-2">Learning Progress</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Typing accuracy and speed statistics</li>
                  <li>Levels completed and achievements earned</li>
                  <li>Character customisation preferences</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-text-dark mb-2">What We DON'T Collect</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Location data</li>
                  <li>Device identifiers</li>
                  <li>Photos or videos</li>
                  <li>Contact lists</li>
                  <li>Browsing history outside TypeTown</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-mint-green" />
              How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-2 text-text-medium ml-2">
              <li>To provide personalised typing lessons based on age and progress</li>
              <li>To track and display learning achievements</li>
              <li>To save game progress and character customisations</li>
              <li>To send important account notifications to parents (optional)</li>
              <li>To process subscription payments securely</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-coral-orange" />
              Data Sharing & Third Parties
            </h2>
            <div className="space-y-4 text-text-medium leading-relaxed">
              <p className="font-bold text-mint-green">
                We DO NOT sell, rent, or share children's personal information with third parties for marketing purposes.
              </p>
              <div>
                <h3 className="font-bold text-text-dark mb-2">Limited Third-Party Services</h3>
                <p className="mb-3">We use the following essential services only:</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-gray-50 p-3 rounded-xl">
                    <CreditCard className="w-5 h-5 text-sky-blue mt-0.5" />
                    <div>
                      <span className="font-bold text-text-dark">Stripe</span>
                      <p className="text-sm">Secure payment processing. Only parent email is shared for receipts. No children's data is sent to Stripe.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gray-50 p-3 rounded-xl">
                    <Mail className="w-5 h-5 text-coral-orange mt-0.5" />
                    <div>
                      <span className="font-bold text-text-dark">Resend</span>
                      <p className="text-sm">Email delivery service. Only used to send welcome emails and account notifications to parents.</p>
                    </div>
                  </div>
                </div>
              </div>
              <p>
                <strong>We never share data with:</strong> Advertisers, data brokers, social media platforms, 
                analytics companies, or any other third parties.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-blue" />
              How We Protect Your Data
            </h2>
            <ul className="list-disc list-inside space-y-2 text-text-medium ml-2">
              <li><strong>Password Encryption:</strong> All passwords are encrypted using bcrypt (industry standard)</li>
              <li><strong>Secure Connections:</strong> All data is transmitted over HTTPS (encrypted)</li>
              <li><strong>Rate Limiting:</strong> Protection against brute force login attempts</li>
              <li><strong>Token Authentication:</strong> Secure JWT tokens that expire after 24 hours</li>
              <li><strong>Restricted Access:</strong> Only authorised personnel can access user data</li>
              <li><strong>No Unnecessary Data:</strong> We only collect what's needed to provide the service</li>
            </ul>
          </section>

          {/* Parental Rights */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4">
              Parental Rights (GDPR & COPPA Compliance)
            </h2>
            <div className="space-y-3 text-text-medium leading-relaxed">
              <p>As a parent or guardian, you have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Review your child's personal information</li>
                <li>Request deletion of your child's account and all associated data</li>
                <li>Refuse further collection of your child's information</li>
                <li>Receive a copy of all data we hold about your child</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:clare@bluecactus.digital" className="text-mint-green font-bold hover:underline">
                  clare@bluecactus.digital
                </a>
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-lg">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4">
              Data Retention
            </h2>
            <p className="text-text-medium leading-relaxed">
              We retain account data for as long as the account is active. If you request account deletion, 
              we will permanently delete all associated personal data within 30 days. Anonymised, aggregated 
              statistics (such as average typing speeds) may be retained for service improvement but cannot 
              be linked back to any individual.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-mint-green/10 rounded-2xl p-6 sm:p-8 border-2 border-mint-green/20">
            <h2 className="text-xl font-display font-bold text-text-dark mb-4">
              Contact Us
            </h2>
            <p className="text-text-medium leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 space-y-2">
              <p className="font-bold text-text-dark">TypeTown / Blue Cactus Digital</p>
              <p>
                <a href="mailto:clare@bluecactus.digital" className="text-mint-green font-bold hover:underline">
                  clare@bluecactus.digital
                </a>
              </p>
            </div>
          </section>

          {/* Back to Home */}
          <div className="text-center pt-4">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-mint-green text-white font-bold rounded-full hover:bg-mint-green/90 transition-colors shadow-lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to TypeTown
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-text-medium text-sm">
          <p>© 2026 TypeTown by Blue Cactus Digital. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
